import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { gestoriaAPI } from '../../services/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  Scale, ArrowLeft, Search, Loader2, Eye, Paperclip, MailCheck,
  Circle, CheckCircle2, Clock, Archive, Trash2, FileText,
  MessageSquare, Send, Shield, User, X, MessageCircle, Download,
} from 'lucide-react';
import NotificationBell from '../../components/NotificationBell';
import { formatApiError } from '../../utils/apiError';

const LABELS_QUIEN = {
  piloto: 'Piloto',
  dueno_aeronave: 'Dueño de Aeronave',
  dueno_tar: 'Dueño de un TAR',
  rt_tar: 'RT / Administrador de un TAR',
  mecanico_tar: 'Mecánico de un TAR',
  otro: 'Otro',
};
const LABELS_TRAMITE = {
  transferencia_aeronaves: 'Transferencia de aeronaves',
  matriculacion: 'Matriculación',
  cese_bandera_certificacion: 'Cese de bandera y certificación',
  habilitacion_tar: 'Habilitación de TAR',
  solicitud_alcances: 'Solicitud de alcances',
  adquisicion_tool_especial: 'Adquisición de Tool Especial',
  licencia_mma_mera: 'Licencia MMA / MERA',
  manual_taller_mpi: 'Manual de Taller (MPI)',
  otro: 'Otro',
};

const ESTADO_BADGE = {
  pendiente: { label: 'Pendiente', classes: 'bg-amber-100 text-amber-800 border-amber-200', icon: Clock },
  en_proceso: { label: 'En proceso', classes: 'bg-blue-100 text-blue-800 border-blue-200', icon: Loader2 },
  resuelto: { label: 'Resuelto', classes: 'bg-emerald-100 text-emerald-800 border-emerald-200', icon: CheckCircle2 },
  archivado: { label: 'Archivado', classes: 'bg-slate-100 text-slate-600 border-slate-200', icon: Archive },
};

const formatDate = (iso) => {
  if (!iso) return '-';
  try {
    const d = new Date(iso);
    return d.toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return iso;
  }
};

const AdminGestoria = () => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    tipo_solicitud: 'all',
    estado: 'all',
    leido: 'all',
    q: '',
    fecha_desde: '',
    fecha_hasta: '',
  });
  const [selected, setSelected] = useState(null);
  const [notas, setNotas] = useState('');
  const [savingNotas, setSavingNotas] = useState(false);

  // Conversación (comentarios)
  const [comentarios, setComentarios] = useState([]);
  const [loadingComs, setLoadingComs] = useState(false);
  const [nuevoMsg, setNuevoMsg] = useState('');
  const [msgFiles, setMsgFiles] = useState([]);
  const [sendingMsg, setSendingMsg] = useState(false);
  const msgFileRef = useRef(null);

  // SIGMA interesados (sub-tab)
  const [sigmaList, setSigmaList] = useState([]);
  const [loadingSigma, setLoadingSigma] = useState(false);

  // Preview modal de adjuntos (Bonus 1) — vía blob URL para UX más fluida
  const [previewAdj, setPreviewAdj] = useState(null); // { solicitudId, id, filename, content_type, size }
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);

  // Liberar blob URLs al desmontar / cambiar
  useEffect(() => {
    return () => { if (previewUrl) URL.revokeObjectURL(previewUrl); };
  }, [previewUrl]);

  const abrirPreviewAdjunto = async (solicitudId, a) => {
    setPreviewAdj({ solicitudId, ...a });
    setPreviewError(null);
    setPreviewLoading(true);
    if (previewUrl) { URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }
    try {
      const apiBase = process.env.REACT_APP_BACKEND_URL || '';
      const token = localStorage.getItem('token');
      const res = await fetch(`${apiBase}/api/admin/gestoria/solicitudes/${solicitudId}/adjuntos/${a.id}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Error ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);
    } catch (e) {
      setPreviewError(e.message || 'No se pudo cargar el adjunto');
    } finally {
      setPreviewLoading(false);
    }
  };

  const cerrarPreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setPreviewAdj(null);
    setPreviewError(null);
  };

  const descargarPreview = () => {
    if (!previewUrl || !previewAdj) return;
    const link = document.createElement('a');
    link.href = previewUrl;
    link.download = previewAdj.filename || 'adjunto';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Guard extra en cliente (el backend también valida)
  const hasAccess = user && (user.role === 'super_admin' || user.email === 'administracion@cmpublicaciones.com');

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (filters.tipo_solicitud !== 'all') params.tipo_solicitud = filters.tipo_solicitud;
      if (filters.estado !== 'all') params.estado = filters.estado;
      if (filters.leido !== 'all') params.leido = filters.leido === 'true';
      if (filters.q.trim()) params.q = filters.q.trim();
      if (filters.fecha_desde) params.fecha_desde = filters.fecha_desde;
      if (filters.fecha_hasta) params.fecha_hasta = filters.fecha_hasta;

      const res = await gestoriaAPI.adminListar(params);
      setItems(res.data?.items || []);
      setTotal(res.data?.total || 0);
    } catch (err) {
      toast.error('Error al cargar solicitudes');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    if (hasAccess) loadList();
  }, [hasAccess, loadList]);

  const openDetail = async (sol) => {
    setSelected(sol);
    setNotas(sol.notas_internas || '');
    setNuevoMsg('');
    setMsgFiles([]);
    // Marcar como leído al abrir (si estaba no leído)
    if (!sol.leido) {
      try {
        await gestoriaAPI.adminActualizar(sol.id, { leido: true });
        setItems((prev) => prev.map((x) => (x.id === sol.id ? { ...x, leido: true } : x)));
      } catch {}
    }
    // Cargar comentarios
    setLoadingComs(true);
    try {
      const res = await gestoriaAPI.adminListarComentarios(sol.id);
      setComentarios(res.data?.items || []);
      // Al abrir el hilo, los mensajes del cliente quedan leídos → resetear badge en la fila
      setItems((prev) => prev.map((x) => (x.id === sol.id ? { ...x, mensajes_no_leidos: 0 } : x)));
    } catch { setComentarios([]); }
    finally { setLoadingComs(false); }
  };

  const handleMsgFiles = (e) => {
    const incoming = Array.from(e.target.files || []);
    if (msgFiles.length + incoming.length > 3) { toast.error('Máximo 3 archivos'); return; }
    const tooBig = incoming.find((f) => f.size > 10 * 1024 * 1024);
    if (tooBig) { toast.error(`"${tooBig.name}" supera los 10MB`); return; }
    const allowed = ['image/png', 'image/jpeg', 'image/jpg', 'application/pdf'];
    const bad = incoming.find((f) => !allowed.includes(f.type));
    if (bad) { toast.error(`Tipo no permitido: "${bad.name}"`); return; }
    setMsgFiles((p) => [...p, ...incoming]);
    e.target.value = '';
  };

  const enviarMensaje = async (e) => {
    e.preventDefault();
    if (!selected) return;
    if (!nuevoMsg.trim() && msgFiles.length === 0) { toast.error('Escribí un mensaje o adjuntá un archivo'); return; }
    setSendingMsg(true);
    try {
      await gestoriaAPI.adminCrearComentario(selected.id, nuevoMsg.trim() || '(Archivo adjunto)', msgFiles);
      setNuevoMsg('');
      setMsgFiles([]);
      toast.success('Mensaje enviado al cliente');
      const res = await gestoriaAPI.adminListarComentarios(selected.id);
      setComentarios(res.data?.items || []);
    } catch (err) {
      toast.error(formatApiError(err, 'No se pudo enviar'));
    } finally { setSendingMsg(false); }
  };

  const cambiarEstado = async (id, estado) => {
    try {
      const res = await gestoriaAPI.adminActualizar(id, { estado });
      setItems((prev) => prev.map((x) => (x.id === id ? res.data : x)));
      if (selected?.id === id) setSelected(res.data);
      toast.success(`Estado: ${ESTADO_BADGE[estado]?.label || estado}`);
    } catch {
      toast.error('No se pudo actualizar el estado');
    }
  };

  const toggleLeido = async (item) => {
    try {
      const res = await gestoriaAPI.adminActualizar(item.id, { leido: !item.leido });
      setItems((prev) => prev.map((x) => (x.id === item.id ? res.data : x)));
    } catch {
      toast.error('No se pudo actualizar');
    }
  };

  const saveNotas = async () => {
    if (!selected) return;
    setSavingNotas(true);
    try {
      const res = await gestoriaAPI.adminActualizar(selected.id, { notas_internas: notas });
      setSelected(res.data);
      setItems((prev) => prev.map((x) => (x.id === selected.id ? res.data : x)));
      toast.success('Notas guardadas');
    } catch {
      toast.error('No se pudieron guardar las notas');
    } finally {
      setSavingNotas(false);
    }
  };

  const loadSigma = useCallback(async () => {
    setLoadingSigma(true);
    try {
      const res = await gestoriaAPI.adminListarSigma();
      setSigmaList(res.data?.items || []);
    } catch {
      toast.error('Error al cargar interesados SIGMA');
    } finally {
      setLoadingSigma(false);
    }
  }, []);

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <Scale className="h-10 w-10 text-slate-400 mx-auto mb-3" />
            <h2 className="font-bold mb-2">Sin acceso</h2>
            <p className="text-sm text-slate-600">Esta sección es exclusiva para Superadministradores y el usuario de Administración.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-gestoria-page">
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-6">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" asChild>
              <Link to="/admin"><ArrowLeft className="h-4 w-4 mr-1" /> Volver</Link>
            </Button>
            <div className="bg-gradient-to-br from-violet-500 to-purple-700 p-2.5 rounded-xl shadow-lg shadow-violet-500/30">
              <Scale className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-slate-900">Gestoría</h1>
              <p className="text-sm text-slate-500">Solicitudes de trámite y asesoramiento</p>
            </div>
          </div>
          <Badge className="bg-violet-100 text-violet-800 border-violet-200 border text-sm self-start md:self-auto">
            {total} solicitud{total === 1 ? '' : 'es'}
          </Badge>
          <NotificationBell className="ml-auto md:ml-0" />
        </div>

        <Tabs defaultValue="solicitudes" className="w-full" onValueChange={(v) => v === 'sigma' && loadSigma()}>
          <TabsList className="bg-white border">
            <TabsTrigger value="solicitudes" data-testid="admin-gestoria-tab-solicitudes">
              <FileText className="mr-2 h-4 w-4" /> Solicitudes
            </TabsTrigger>
            <TabsTrigger value="sigma" data-testid="admin-gestoria-tab-sigma">
              <MailCheck className="mr-2 h-4 w-4" /> Interesados SIGMA
            </TabsTrigger>
          </TabsList>

          <TabsContent value="solicitudes" className="mt-4 space-y-4">
            {/* Filtros */}
            <Card>
              <CardContent className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-3">
                <div className="lg:col-span-2">
                  <Label className="text-xs">Búsqueda</Label>
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      value={filters.q}
                      onChange={(e) => setFilters((p) => ({ ...p, q: e.target.value }))}
                      placeholder="Nombre, email, DNI, matrícula..."
                      className="pl-8"
                      data-testid="admin-gestoria-search"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Tipo</Label>
                  <Select value={filters.tipo_solicitud} onValueChange={(v) => setFilters((p) => ({ ...p, tipo_solicitud: v }))}>
                    <SelectTrigger data-testid="admin-gestoria-filter-tipo"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      <SelectItem value="tramite">Trámite</SelectItem>
                      <SelectItem value="asesoramiento">Asesoramiento</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Estado</Label>
                  <Select value={filters.estado} onValueChange={(v) => setFilters((p) => ({ ...p, estado: v }))}>
                    <SelectTrigger data-testid="admin-gestoria-filter-estado"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      <SelectItem value="pendiente">Pendiente</SelectItem>
                      <SelectItem value="en_proceso">En proceso</SelectItem>
                      <SelectItem value="resuelto">Resuelto</SelectItem>
                      <SelectItem value="archivado">Archivado</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Leído</Label>
                  <Select value={filters.leido} onValueChange={(v) => setFilters((p) => ({ ...p, leido: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      <SelectItem value="false">No leídos</SelectItem>
                      <SelectItem value="true">Leídos</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Desde</Label>
                  <Input type="date" value={filters.fecha_desde} onChange={(e) => setFilters((p) => ({ ...p, fecha_desde: e.target.value }))} />
                </div>
                <div className="lg:col-span-5 grid grid-cols-2 gap-2 items-end">
                  <div>
                    <Label className="text-xs">Hasta</Label>
                    <Input type="date" value={filters.fecha_hasta} onChange={(e) => setFilters((p) => ({ ...p, fecha_hasta: e.target.value }))} />
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => setFilters({ tipo_solicitud: 'all', estado: 'all', leido: 'all', q: '', fecha_desde: '', fecha_hasta: '' })}
                  >
                    Limpiar
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Tabla */}
            <Card>
              <CardContent className="p-0 overflow-x-auto">
                {loading ? (
                  <div className="p-10 text-center text-slate-500">
                    <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
                    Cargando...
                  </div>
                ) : items.length === 0 ? (
                  <div className="p-12 text-center text-slate-500">
                    <Scale className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                    <p>No hay solicitudes que coincidan con los filtros.</p>
                  </div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b">
                      <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                        <th className="p-3 w-10"></th>
                        <th className="p-3">Fecha</th>
                        <th className="p-3">Tipo</th>
                        <th className="p-3">Solicitante</th>
                        <th className="p-3">Trámite / Motivo</th>
                        <th className="p-3">TAR</th>
                        <th className="p-3">Estado</th>
                        <th className="p-3 w-24 text-right">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((it) => {
                        const EstadoIcon = ESTADO_BADGE[it.estado]?.icon || Circle;
                        return (
                          <tr
                            key={it.id}
                            className={`border-b last:border-0 hover:bg-slate-50 transition-colors ${!it.leido ? 'bg-violet-50/50' : ''}`}
                            data-testid={`admin-gestoria-row-${it.id}`}
                          >
                            <td className="p-3">
                              <button onClick={() => toggleLeido(it)} title={it.leido ? 'Marcar no leído' : 'Marcar leído'}>
                                {it.leido ? <Circle className="h-4 w-4 text-slate-300" /> : <Circle className="h-4 w-4 text-violet-500 fill-violet-500" />}
                              </button>
                            </td>
                            <td className="p-3 whitespace-nowrap text-slate-700">{formatDate(it.created_at)}</td>
                            <td className="p-3">
                              <Badge variant="outline" className={it.tipo_solicitud === 'tramite' ? 'border-violet-200 text-violet-700 bg-violet-50' : 'border-sky-200 text-sky-700 bg-sky-50'}>
                                {it.tipo_solicitud === 'tramite' ? 'Trámite' : 'Asesoramiento'}
                              </Badge>
                            </td>
                            <td className="p-3">
                              <p className={`${!it.leido ? 'font-semibold' : 'font-medium'} text-slate-900 flex items-center gap-1.5`}>
                                {it.nombre_completo}
                                {it.mensajes_no_leidos > 0 && (
                                  <span
                                    title={`${it.mensajes_no_leidos} mensaje(s) nuevo(s) del cliente`}
                                    className="inline-flex items-center gap-1 bg-violet-100 text-violet-700 border border-violet-300 text-xs font-semibold px-1.5 py-0.5 rounded-full"
                                    data-testid={`admin-gestoria-msg-badge-${it.id}`}
                                  >
                                    <MessageCircle className="h-3 w-3" />
                                    {it.mensajes_no_leidos}
                                  </span>
                                )}
                              </p>
                              <p className="text-xs text-slate-500">{it.email}</p>
                            </td>
                            <td className="p-3 text-slate-700 max-w-[200px] truncate" title={LABELS_TRAMITE[it.tipo_tramite]}>
                              {it.tipo_tramite === 'otro' ? `Otro: ${it.tipo_tramite_otro || '-'}` : LABELS_TRAMITE[it.tipo_tramite]}
                            </td>
                            <td className="p-3 text-slate-600 max-w-[180px] truncate" title={it.tar_razon_social || it.tar_texto}>
                              {it.tar_razon_social || it.tar_texto || <span className="text-slate-400">—</span>}
                            </td>
                            <td className="p-3">
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-medium ${ESTADO_BADGE[it.estado]?.classes}`}>
                                <EstadoIcon className="h-3 w-3" />
                                {ESTADO_BADGE[it.estado]?.label || it.estado}
                              </span>
                            </td>
                            <td className="p-3 text-right">
                              <Button size="sm" variant="ghost" onClick={() => openDetail(it)} data-testid={`admin-gestoria-view-${it.id}`}>
                                <Eye className="h-4 w-4" />
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="sigma" className="mt-4">
            <Card>
              <CardContent className="p-0">
                {loadingSigma ? (
                  <div className="p-10 text-center text-slate-500">
                    <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" /> Cargando...
                  </div>
                ) : sigmaList.length === 0 ? (
                  <div className="p-12 text-center text-slate-500">
                    <MailCheck className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                    <p>Aún no hay interesados registrados.</p>
                  </div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b">
                      <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                        <th className="p-3">Fecha</th>
                        <th className="p-3">Nombre</th>
                        <th className="p-3">Email</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sigmaList.map((it) => (
                        <tr key={it.id} className="border-b last:border-0">
                          <td className="p-3 whitespace-nowrap text-slate-700">{formatDate(it.created_at)}</td>
                          <td className="p-3 font-medium">{it.nombre}</td>
                          <td className="p-3 text-slate-600">{it.email}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Detalle */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="admin-gestoria-detail">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Scale className="h-5 w-5 text-violet-600" />
                  {selected.tipo_solicitud === 'tramite' ? 'Solicitud de Trámite' : 'Solicitud de Asesoramiento'}
                </DialogTitle>
                <p className="text-xs text-slate-500">Recibida {formatDate(selected.created_at)}</p>
              </DialogHeader>

              <div className="space-y-4">
                <div>
                  <Label className="text-xs">Estado</Label>
                  <Select value={selected.estado} onValueChange={(v) => cambiarEstado(selected.id, v)}>
                    <SelectTrigger className="max-w-xs" data-testid="admin-gestoria-detail-estado"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pendiente">Pendiente</SelectItem>
                      <SelectItem value="en_proceso">En proceso</SelectItem>
                      <SelectItem value="resuelto">Resuelto</SelectItem>
                      <SelectItem value="archivado">Archivado</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid md:grid-cols-2 gap-3 text-sm">
                  <div>
                    <Label className="text-xs">Solicitante</Label>
                    <p className="font-medium">{selected.nombre_completo}</p>
                    <p className="text-slate-500 text-xs">DNI {selected.dni}</p>
                  </div>
                  <div>
                    <Label className="text-xs">Contacto</Label>
                    <p><a href={`mailto:${selected.email}`} className="text-violet-600 hover:underline">{selected.email}</a></p>
                    <p><a href={`tel:${selected.telefono}`} className="text-violet-600 hover:underline">{selected.telefono}</a></p>
                  </div>
                  <div>
                    <Label className="text-xs">¿Quién nos contacta?</Label>
                    <p>{selected.quien_contacta === 'otro' ? `Otro: ${selected.quien_contacta_otro}` : LABELS_QUIEN[selected.quien_contacta]}</p>
                  </div>
                  <div>
                    <Label className="text-xs">{selected.tipo_solicitud === 'tramite' ? 'Tipo de trámite' : 'Motivo de consulta'}</Label>
                    <p>{selected.tipo_tramite === 'otro' ? `Otro: ${selected.tipo_tramite_otro}` : LABELS_TRAMITE[selected.tipo_tramite]}</p>
                  </div>
                  {(selected.tar_razon_social || selected.tar_texto) && (
                    <div>
                      <Label className="text-xs">TAR</Label>
                      <p>{selected.tar_razon_social || selected.tar_texto}</p>
                      {selected.tar_anac && <p className="text-xs text-slate-500">ANAC {selected.tar_anac}</p>}
                      {!selected.tar_razon_social && <p className="text-xs text-amber-600">No registrado en sistema</p>}
                    </div>
                  )}
                  {selected.matricula && (
                    <div>
                      <Label className="text-xs">Matrícula</Label>
                      <p>{selected.matricula}</p>
                    </div>
                  )}
                </div>

                <div>
                  <Label className="text-xs">{selected.tipo_solicitud === 'tramite' ? 'Nota' : 'Consulta'}</Label>
                  <div className="bg-slate-50 border rounded-md p-3 whitespace-pre-wrap text-sm text-slate-800">
                    {selected.texto}
                  </div>
                </div>

                {selected.adjuntos?.length > 0 && (
                  <div>
                    <Label className="text-xs">Adjuntos ({selected.adjuntos.length})</Label>
                    <div className="space-y-1.5">
                      {selected.adjuntos.map((a) => (
                        <button
                          key={a.id}
                          type="button"
                          onClick={() => abrirPreviewAdjunto(selected.id, a)}
                          className="w-full flex items-center gap-2 p-2 border rounded-md hover:bg-violet-50 hover:border-violet-200 transition-colors text-sm text-left"
                          data-testid={`admin-gestoria-adj-${a.id}`}
                        >
                          <Paperclip className="h-4 w-4 text-violet-500" />
                          <span className="flex-1 truncate">{a.filename}</span>
                          <span className="text-xs text-slate-500">{(a.size / 1024).toFixed(0)} KB</span>
                          <Eye className="h-3.5 w-3.5 text-violet-400" />
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <Label className="text-xs flex items-center gap-1">
                    <MessageSquare className="h-3.5 w-3.5 text-violet-600" /> Conversación con el cliente
                  </Label>
                  <div className="border rounded-md bg-slate-50 p-3 mt-1 max-h-[320px] overflow-y-auto space-y-3">
                    {loadingComs ? (
                      <div className="text-center text-slate-500 py-4"><Loader2 className="h-4 w-4 animate-spin mx-auto" /></div>
                    ) : comentarios.length === 0 ? (
                      <p className="text-sm text-slate-500 italic text-center py-2">Sin mensajes todavía</p>
                    ) : comentarios.map((c) => {
                      const esAdmin = c.autor === 'admin';
                      return (
                        <div key={c.id} className={`flex gap-2 ${esAdmin ? 'flex-row-reverse' : ''}`}>
                          <div className={`flex-shrink-0 h-7 w-7 rounded-full flex items-center justify-center ${esAdmin ? 'bg-violet-600' : 'bg-slate-400'}`}>
                            {esAdmin ? <Shield className="h-3.5 w-3.5 text-white" /> : <User className="h-3.5 w-3.5 text-white" />}
                          </div>
                          <div className={`flex-1 max-w-[80%] ${esAdmin ? 'text-right' : ''}`}>
                            <div className={`inline-block text-left rounded-xl px-3 py-2 ${esAdmin ? 'bg-violet-100 border border-violet-200' : 'bg-white border border-slate-200'}`}>
                              <p className="text-xs font-medium mb-1 text-slate-700">{esAdmin ? (c.autor_nombre || 'Equipo C&M') : (c.autor_nombre || 'Cliente')}</p>
                              <p className="text-sm text-slate-800 whitespace-pre-wrap">{c.texto}</p>
                              {c.adjuntos?.length > 0 && (
                                <div className="mt-1.5 space-y-1">
                                  {c.adjuntos.map((a) => (
                                    <button
                                      key={a.id}
                                      type="button"
                                      onClick={() => abrirPreviewAdjunto(selected.id, a)}
                                      className="flex items-center gap-1.5 text-xs px-2 py-1 rounded border bg-white hover:bg-slate-50"
                                      data-testid={`admin-gestoria-msg-adj-${a.id}`}
                                    >
                                      <Paperclip className="h-3 w-3 text-violet-500" />
                                      <span className="truncate">{a.filename}</span>
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                            <p className="text-xs text-slate-400 mt-0.5 px-1">{new Date(c.created_at).toLocaleString('es-AR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <form onSubmit={enviarMensaje} className="mt-3 space-y-2">
                    <Textarea
                      value={nuevoMsg}
                      onChange={(e) => setNuevoMsg(e.target.value)}
                      placeholder="Responderle al cliente..."
                      rows={2}
                      data-testid="admin-gestoria-msg-texto"
                    />
                    <div className="flex items-center gap-2 flex-wrap">
                      <Button type="button" size="sm" variant="outline" onClick={() => msgFileRef.current?.click()} disabled={msgFiles.length >= 3} data-testid="admin-gestoria-msg-attach">
                        <Paperclip className="mr-1 h-4 w-4" /> Adjuntar
                      </Button>
                      <span className="text-xs text-slate-500">{msgFiles.length}/3 • PNG, JPG, PDF</span>
                      <input ref={msgFileRef} type="file" hidden multiple accept=".png,.jpg,.jpeg,.pdf,image/png,image/jpeg,application/pdf" onChange={handleMsgFiles} />
                      <Button type="submit" size="sm" disabled={sendingMsg} className="ml-auto bg-violet-600 hover:bg-violet-500" data-testid="admin-gestoria-msg-submit">
                        {sendingMsg ? <><Loader2 className="mr-1 h-4 w-4 animate-spin" /> Enviando</> : <><Send className="mr-1 h-4 w-4" /> Enviar al cliente</>}
                      </Button>
                    </div>
                    {msgFiles.length > 0 && (
                      <div className="space-y-1">
                        {msgFiles.map((f, i) => (
                          <div key={i} className="flex items-center justify-between bg-white border rounded px-2 py-1 text-xs">
                            <span className="truncate">{f.name}</span>
                            <button type="button" onClick={() => setMsgFiles((p) => p.filter((_, idx) => idx !== i))} className="text-slate-400 hover:text-red-500">
                              <X className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </form>
                </div>

                <div>
                  <Label className="text-xs">Notas internas</Label>
                  <Textarea
                    value={notas}
                    onChange={(e) => setNotas(e.target.value)}
                    rows={3}
                    placeholder="Solo visible para el equipo interno..."
                    data-testid="admin-gestoria-notas"
                  />
                  <div className="flex justify-end mt-2">
                    <Button size="sm" onClick={saveNotas} disabled={savingNotas} className="bg-violet-600 hover:bg-violet-500">
                      {savingNotas ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Guardando...</> : 'Guardar notas'}
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Preview modal de adjuntos (Bonus 1) */}
      <Dialog open={!!previewAdj} onOpenChange={(o) => { if (!o) cerrarPreview(); }}>
        <DialogContent className="max-w-5xl max-h-[92vh] p-0 overflow-hidden" data-testid="admin-gestoria-preview">
          {previewAdj && (
            <>
              <DialogHeader className="px-5 pt-4 pb-3 border-b">
                <div className="flex items-center gap-3 pr-8">
                  <Paperclip className="h-4 w-4 text-violet-600 flex-shrink-0" />
                  <DialogTitle className="text-base font-medium truncate flex-1">{previewAdj.filename}</DialogTitle>
                  <span className="text-xs text-slate-500 flex-shrink-0">
                    {previewAdj.content_type || 'archivo'} · {(previewAdj.size / 1024).toFixed(0)} KB
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={descargarPreview}
                    disabled={!previewUrl}
                    data-testid="admin-gestoria-preview-download"
                  >
                    <Download className="h-3.5 w-3.5 mr-1" />
                    Descargar
                  </Button>
                </div>
              </DialogHeader>

              <div className="bg-slate-100 h-[75vh] flex items-center justify-center overflow-auto">
                {previewLoading && (
                  <div className="flex flex-col items-center gap-2 text-slate-500">
                    <Loader2 className="h-6 w-6 animate-spin text-violet-500" />
                    <span className="text-sm">Cargando adjunto...</span>
                  </div>
                )}
                {previewError && (
                  <div className="flex flex-col items-center gap-2 text-red-600 px-6 text-center">
                    <X className="h-8 w-8" />
                    <p className="font-medium">Error: {previewError}</p>
                    <p className="text-xs text-slate-500">Probá refrescar la página o contactá al equipo técnico.</p>
                  </div>
                )}
                {!previewLoading && !previewError && previewUrl && (() => {
                  const ct = (previewAdj.content_type || '').toLowerCase();
                  if (ct.startsWith('image/')) {
                    return (
                      <img
                        src={previewUrl}
                        alt={previewAdj.filename}
                        className="max-w-full max-h-full object-contain"
                      />
                    );
                  }
                  if (ct === 'application/pdf') {
                    return (
                      <iframe
                        src={previewUrl}
                        title={previewAdj.filename}
                        className="w-full h-full bg-white"
                      />
                    );
                  }
                  return (
                    <div className="text-center px-6">
                      <FileText className="h-12 w-12 text-slate-400 mx-auto mb-2" />
                      <p className="text-sm text-slate-600">No se puede previsualizar este tipo de archivo.</p>
                      <Button size="sm" variant="default" className="mt-3 bg-violet-600 hover:bg-violet-500" onClick={descargarPreview}>
                        <Download className="h-3.5 w-3.5 mr-1" /> Descargar para abrirlo
                      </Button>
                    </div>
                  );
                })()}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminGestoria;
